import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { localize } from 'common/utils';
import { firstOrNull } from 'common/utils/array';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { useAdCampaignTranslationKeys } from 'modules/campaigns/ad-campaign/hooks/useAdCampaignTranslationKeys';
import { TERMS_AND_CONDITIONS_LINK } from 'modules/campaigns/constants';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { AdCampaign } from 'modules/campaigns/types';
import { TestIds } from 'modules/campaigns/utils/testUtils';

import CampaignFormItem from '../../../components/CampaignFormItem';
import CampaignSidebarLayout from '../../../components/CampaignSidebarLayout';
import CopyCampaignIdButton from '../../../components/CopyCampaignIdButton';
import { HelperText } from '../../../components/HelperText';
import { AgreementsDetails } from '../../../components/agreements-section/AgreementsDetails';
import { useGetCampaignAuthor } from '../AdCampaignForm/hooks/useGetCampaignAuthor';
import { AdsCampaignWeeklyBudgetDetails } from '../budget-section/AdsCampaignWeeklyBudgetDetails';
import { ExistingFreeTrialPlaceholder } from '../free-trials/form-components/ExistingFreeTrialPlaceholder';
import { AdCampaignPreview } from '../preview-section/AdCampaignPreview';
import { AdCampaignScheduleDetails } from '../schedule-section/AdCampaignScheduleDetails';

export type AdCampaignDetailsProps = {
  campaign: AdCampaign;
};

export const AdCampaignDetails = ({ campaign }: AdCampaignDetailsProps) => {
  const adTranslationKeys = useAdCampaignTranslationKeys();
  const { currency, venues } = useCampaignModuleContext();

  const { formatMessage, locale } = useIntl();

  const campaignAuthor = campaign.created_by;
  const { author, isFetching: isLoadingAuthor } = useGetCampaignAuthor({
    authorName: campaignAuthor || '',
    isEnabled: !!campaignAuthor,
  });

  const venueToPreview = firstOrNull(venues);
  const isFreeTrial = 'is_free_trial' in campaign && campaign.is_free_trial;

  return (
    <CampaignSidebarLayout
      preview={
        venueToPreview && (
          <AdCampaignPreview
            venue={{
              name: localize(venueToPreview.name, locale),
              description: localize(venueToPreview.short_description, locale),
              imageUrl: venueToPreview.listimage,
            }}
          />
        )
      }
      tooltip="campaigns.sidebar-preview.ads.tooltip"
      isFloatingBannerEnabled
      infoBannerContent={{
        title: formatMessage({ id: 'campaigns.ads.info-banner.title' }),
        text: formatMessage({ id: adTranslationKeys.infoBannerText }),
        campaignId: campaign.id,
      }}
    >
      {isFreeTrial && <ExistingFreeTrialPlaceholder />}
      <CampaignFormItem
        data-test-id="budget-field-item"
        title={<FormattedMessage id={adTranslationKeys.budgetTitle} />}
        tooltip={<FormattedMessage id="campaigns.form.budget.tooltip" />}
        value={
          <>
            <AdsCampaignWeeklyBudgetDetails
              bidAmount={
                // bid strategy is in lowercase after saving changes in the edit page so use toUpperCase here, after reload it is in uppercase again
                campaign.bid_strategy.toUpperCase() === 'CUSTOM' && campaign.bid_amount
                  ? convertMonetaryIntToDecimal(campaign.bid_amount, currency)
                  : undefined
              }
              budgetAmount={convertMonetaryIntToDecimal(campaign.weekly_budget_amount, currency)}
            />
            <HelperText text={formatMessage({ id: 'campaigns.ads.budget.vat-text' })} />
          </>
        }
      />
      <AdCampaignScheduleDetails startDate={campaign.start_date} endDate={campaign.end_date} />
      <AgreementsDetails
        agreements={[
          <FormattedMessage
            id={adTranslationKeys.agreementStatement}
            values={{
              link: (
                <a href={TERMS_AND_CONDITIONS_LINK} target="_blank" rel="noreferrer">
                  <FormattedMessage id="campaigns.agreements.terms-and-conditions-link" />
                </a>
              ),
            }}
          />,
        ]}
        isLoading={isLoadingAuthor}
        author={author}
        isCreatedByWolt={author?.email.endsWith('@wolt.com')} // TODO: @jjanii replade with campaign.created_by_wolt when it will be available
      />
      <CopyCampaignIdButton dataTestId={TestIds.DETAILS.ID.WRAPPER} campaignId={campaign.id} />
    </CampaignSidebarLayout>
  );
};
